import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { Loader2, Paperclip, X } from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Label } from '@meno/ui/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@meno/ui/components/ui/dialog'
import { requestScreenRenewal } from '@meno/ui/lib/api'

// Ask for an expired screen's license to be renewed.
//
// The message starts pre-written rather than empty. An operator opening this is
// trying to get a dark screen back on, not compose a procurement email, and a
// blank required box is where that stalls — so the template says the useful
// part (which screen, when it expired) and they can add to it or send as-is.
//
// The attachment is optional and exists because renewals are usually gated on
// paperwork: a PO number, a signed quote, a payment receipt. Without it that
// document arrives in a separate email thread and stops being attached to the
// request anyone is working from.

// Mirrors the backend allow-list in screens_pairing.py — deliberately narrow.
const ACCEPT = 'application/pdf,image/png,image/jpeg,image/webp'
const MAX_BYTES = 10 * 1024 * 1024

export default function RenewalRequestDialog({
  open, onOpenChange, publicId, screenName, expiresAt, onSubmitted,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  publicId: string
  screenName: string
  expiresAt?: string | null
  onSubmitted?: () => void
}) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)

  // Re-seed the template each time it opens: the screen or its expiry may have
  // changed since last time, and a half-typed message from a previous, possibly
  // different screen should not be waiting here.
  useEffect(() => {
    if (!open) return
    const when = expiresAt ? new Date(expiresAt).toLocaleDateString() : null
    setReason(
      when
        ? t('screens.renewal.template', {
            defaultValue: 'Please renew the license for "{{name}}", which expired on {{when}}.',
            name: screenName, when,
          })
        : t('screens.renewal.templateNoDate', {
            defaultValue: 'Please renew the license for "{{name}}".',
            name: screenName,
          }),
    )
    setFile(null)
    if (fileRef.current) fileRef.current.value = ''
  }, [open, screenName, expiresAt, t])

  const pickFile = (f: File | null) => {
    if (f && f.size > MAX_BYTES) {
      // Checked here as well as server-side so the operator finds out before
      // waiting on a 10 MB upload that was always going to be refused.
      toast.error(t('screens.renewal.tooLarge', 'That file is over 10 MB.'))
      if (fileRef.current) fileRef.current.value = ''
      return
    }
    setFile(f)
  }

  const submit = async () => {
    setBusy(true)
    try {
      await requestScreenRenewal(publicId, reason, file)
      toast.success(t('screens.renewal.sent', 'Renewal requested. Our team will be in touch.'))
      onOpenChange(false)
      onSubmitted?.()
    } catch (err: any) {
      toast.error(
        err?.response?.data?.detail
        ?? t('screens.renewal.failed', 'Could not send the renewal request.'),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={v => { if (!busy) onOpenChange(v) }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('screens.renewal.title', 'Request license renewal')}</DialogTitle>
          <DialogDescription>
            {t('screens.renewal.subtitle', 'We will pick this up and get back to you. Attach a PO or receipt if you have one.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="renewal-reason">{t('screens.renewal.message', 'Message')}</Label>
            <textarea
              id="renewal-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={4}
              maxLength={500}
              className="w-full rounded-md border border-border bg-muted/10 px-3 py-2 text-sm
                         focus:outline-none focus-visible:ring-2 focus-visible:ring-ring resize-y"
            />
            <p className="text-xs text-muted-foreground text-end tabular-nums">{reason.length}/500</p>
          </div>

          <div className="grid gap-1.5">
            <Label>{t('screens.renewal.attachment', 'Attachment (optional)')}</Label>
            {file ? (
              <div className="flex items-center gap-2 rounded-md border border-border bg-muted/20 px-3 py-2">
                <Paperclip className="w-4 h-4 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-sm">{file.name}</span>
                <button
                  type="button"
                  onClick={() => pickFile(null)}
                  aria-label={t('common.remove', 'Remove')}
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <Button
                type="button" variant="outline"
                onClick={() => fileRef.current?.click()}
                className="justify-start gap-2 font-normal"
              >
                <Paperclip className="w-4 h-4" />
                {t('screens.renewal.attach', 'Attach a PDF or image')}
              </Button>
            )}
            <input
              ref={fileRef} type="file" accept={ACCEPT} className="hidden"
              onChange={e => pickFile(e.target.files?.[0] ?? null)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy && <Loader2 className="w-4 h-4 animate-spin me-2" />}
            {t('screens.renewal.send', 'Send request')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
